import { useState, useCallback } from 'react';
import { DataSet, createColumnName } from '../../types';
import { Report, DataTableElement } from '../../types/report.types';
import { createReportElementId, createReportId, ReportElementId } from '../../types/id.types';
import { useProjectStore } from '../stores/projectStore';
import { appendElementReference } from '../utils/reportMarkdownParser';
import logger from '../utils/logger';

/**
 * データテーブルのレポート追加機能を管理するカスタムフック
 */
export function useDataTableReport(dataset: DataSet) {
  const [showReportModal, setShowReportModal] = useState(false);
  const { project, updateProject } = useProjectStore();

  /**
   * レポートモーダルを開く
   */
  const openReportModal = useCallback(() => {
    logger.info('[useDataTableReport] Opening report modal', {
      datasetId: dataset.id,
      datasetName: dataset.name
    });
    setShowReportModal(true);
  }, [dataset]);

  /**
   * レポートモーダルを閉じる
   */
  const closeReportModal = useCallback(() => {
    logger.info('[useDataTableReport] Closing report modal');
    setShowReportModal(false);
  }, []);

  /**
   * 新規レポートを作成
   */
  const createNewReport = useCallback((name: string, description: string): Report => {
    logger.info('[useDataTableReport] Creating new report', { name, description });
    
    const newReport: Report = {
      id: createReportId(`report-${crypto.randomUUID()}`),
      name: name || `Data Table Report - ${new Date().toLocaleDateString()}`,
      description: description || 'Created from Data Table',
      content: '',
      elements: {},
      reportSettings: {
        contentWidth: 800,
        defaultElementMargin: 16,
        backgroundColor: '#ffffff',
        autoSave: false,
        autoSaveInterval: 5
      },
      metadata: {
        createdAt: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        createdBy: 'DataTable',
        sharing: {
          isPublic: false,
          permissions: ['view', 'edit'] as const
        }
      }
    };

    return newReport;
  }, []);

  /**
   * DataTableElementを作成
   */
  const createDataTableElement = useCallback((
    displayColumns: string[],
    maxRows: number
  ): DataTableElement => {
    const elementId = createReportElementId(`table-${crypto.randomUUID()}`) as ReportElementId;
    
    const dataTableElement: DataTableElement = {
      id: elementId,
      type: 'data_table',
      displayColumns: displayColumns.map(createColumnName),
      maxRows,
      datasetId: dataset.id,
      height: Math.min(400, 41 * (maxRows + 1)), // ヘッダー + 行数 * 行高さ（最大400px）
      marginBottom: 16,
      formatting: {
        headerStyle: {
          backgroundColor: '#f3f4f6',
          fontWeight: 'bold',
          padding: '8px',
          borderBottom: '2px solid #d1d5db'
        },
        cellStyle: {
          padding: '8px',
          borderBottom: '1px solid #e5e7eb'
        },
        alternateRowColor: '#f9fafb'
      },
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };

    logger.info('[useDataTableReport] Created DataTableElement', {
      elementId,
      displayColumns: displayColumns.length,
      maxRows,
      datasetId: dataset.id
    });

    return dataTableElement;
  }, [dataset]);

  /**
   * データテーブルをレポートに追加
   */
  const addDataTableToReport = useCallback((
    reportId: string | null,
    newReportName: string,
    newReportDescription: string,
    displayColumns: string[],
    maxRows: number
  ) => {
    if (!project) {
      logger.error('[useDataTableReport] No project available');
      return;
    }

    logger.info('[useDataTableReport] Adding data table to report', {
      reportId,
      newReportName,
      displayColumnsCount: displayColumns.length,
      maxRows
    });

    // DataTableElementを作成
    const dataTableElement = createDataTableElement(displayColumns, maxRows);

    // プロジェクトを更新
    updateProject((proj) => {
      let targetReportId = reportId;
      let updatedReports = [...(proj.reports || [])];

      // 新規レポートを作成する場合
      if (!targetReportId) {
        const newReport = createNewReport(newReportName, newReportDescription);
        updatedReports.push(newReport);
        targetReportId = newReport.id;
        logger.info('[useDataTableReport] Created new report', { reportId: newReport.id });
      }

      // レポートにDataTableElementを追加
      updatedReports = updatedReports.map(report => {
        if (report.id === targetReportId) {
          return {
            ...report,
            content: appendElementReference(report.content, dataTableElement.type, dataTableElement.id),
            elements: {
              ...report.elements,
              [dataTableElement.id]: dataTableElement
            },
            metadata: {
              ...report.metadata,
              lastModified: new Date().toISOString()
            }
          };
        }
        return report;
      });

      logger.info('[useDataTableReport] Added data table to report', {
        reportId: targetReportId,
        elementId: dataTableElement.id
      });

      return {
        ...proj,
        reports: updatedReports
      };
    });

    // モーダルを閉じる
    closeReportModal();
  }, [project, updateProject, createNewReport, createDataTableElement, closeReportModal]);

  return {
    showReportModal,
    reports: project?.reports || [],
    openReportModal,
    closeReportModal,
    addDataTableToReport
  };
}